import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Globe, ArrowRight } from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";

// Shared applicability statement shown on both the Privacy Policy and the
// Terms of Use — makes clear the documents cover web + official iOS/Android apps.
const APPLICABILITY =
  "This Privacy Policy and these Terms of Use apply to all users of WhooLive, whether they access the Service through the website www.whoolive.com or through the official mobile applications for iOS and Android. These platforms may share the same accounts, data, content and services.";

export function LegalPage({
  testidPrefix,
  badgeLabel = "Legal",
  title,
  lastUpdated,
  intro,
  sections = [],
  crossLink,
}) {
  return (
    <MainLayout>
      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16">
        {/* Header */}
        <div data-testid={`${testidPrefix}-header`}>
          <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-brand">
            <ShieldCheck className="h-4 w-4" /> {badgeLabel}
          </span>
          <h1
            data-testid={`${testidPrefix}-title`}
            className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl"
          >
            {title}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
          {intro && <p className="mt-4 text-base text-muted-foreground">{intro}</p>}
        </div>

        {/* Applicability — applies to web + official mobile apps */}
        <div
          data-testid={`${testidPrefix}-applicability`}
          className="mt-8 flex items-start gap-3 rounded-2xl border border-brand/20 bg-brand/5 p-5"
        >
          <Globe className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
          <p className="text-sm leading-relaxed text-foreground">{APPLICABILITY}</p>
        </div>

        {/* Cross-link to the companion legal document */}
        {crossLink && (
          <Link
            to={crossLink.to}
            data-testid={`${testidPrefix}-crosslink-top`}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-hover"
          >
            {crossLink.label} <ArrowRight className="h-4 w-4" />
          </Link>
        )}

        {/* Sections */}
        <div className="mt-10 space-y-9">
          {sections.map((s) => (
            <section key={s.id} data-testid={`${testidPrefix}-section-${s.id}`}>
              <h2 className="font-heading text-lg font-extrabold tracking-tight text-foreground md:text-xl">
                {s.title}
              </h2>
              <div className="mt-3 space-y-3">
                {s.body.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Cross-link footer */}
        {crossLink && (
          <div className="mt-12 rounded-2xl border border-border bg-secondary/50 p-6">
            <p className="text-sm text-muted-foreground">{crossLink.desc}</p>
            <Link
              to={crossLink.to}
              data-testid={`${testidPrefix}-crosslink-bottom`}
              className="mt-2 inline-flex items-center gap-1.5 font-semibold text-brand hover:text-brand-hover"
            >
              {crossLink.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

export default LegalPage;
